import React from 'react';
import { Box, TextField, IconButton, Tooltip, InputAdornment } from '@mui/material';
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import CloseIcon from '@mui/icons-material/Close';
import { TaskEditFormProps } from '../types';
import { DIVISION_SETTINGS_TEXTFIELD_LIMIT, INVALID_MILESTONE_TASK_NAME_MATCH, INVALID_TASK_EXISTS_IN_MILESTONE } from '../../../../../../../../utils/constants';
import { ClearIcon } from '@mui/x-date-pickers';

export const TaskEditForm: React.FC<TaskEditFormProps> = ({
     taskId,
     taskName,
     onSave,
     onCancel,
     existingTaskNames,
     currentMilestones = [],
     milestoneName
}) => {
     const [editingTaskName, setEditingTaskName] = React.useState<string>(taskName);

     const handleSave = () => {
          if (editingTaskName.trim()) {
               onSave(taskId, editingTaskName.trim());
          }
     };

     const handleCancel = () => {
          setEditingTaskName(taskName);
          onCancel();
     };

     const handleKeyPress = (e: React.KeyboardEvent) => {
          if (e.key === 'Enter') {
               handleSave();
          } else if (e.key === 'Escape') {
               handleCancel();
          }
     };

     const editingTaskNameTrim = editingTaskName.trim();
     const taskNameTrim = taskName.trim();
     // Find which milestone contains the duplicate task, excluding the current task being edited
     const duplicateTaskMilestone = currentMilestones.find(
          milestone => milestone.taskName?.toLowerCase().trim() === editingTaskNameTrim.toLowerCase() &&
          editingTaskNameTrim.toLowerCase() !== taskNameTrim.toLowerCase()
     );
     const taskDuplicate = !!duplicateTaskMilestone;
     const duplicateMilestoneName = duplicateTaskMilestone?.mileStoneName || '';
     const namesMatch = milestoneName.toLowerCase().trim() === editingTaskNameTrim.toLowerCase();

     return (
          <Box>
               <TextField
                    size="small"
                    value={editingTaskName}
                    onChange={(e) => setEditingTaskName(e.target.value)}
                    onKeyDown={handleKeyPress}
                    autoFocus
                    variant="standard"
                    sx={{
                         minWidth: '120px',
                         '& .MuiInputBase-root': {
                              backgroundColor: '#fff',
                              borderRadius: 1,
                         },
                    }}
                    inputProps={{ maxLength: DIVISION_SETTINGS_TEXTFIELD_LIMIT }}
                    error={taskDuplicate || namesMatch}
                    helperText={
                         taskDuplicate
                              ? `${INVALID_TASK_EXISTS_IN_MILESTONE} in "${duplicateMilestoneName}"`
                              : namesMatch
                              ? INVALID_MILESTONE_TASK_NAME_MATCH
                              : `${editingTaskName.length}/${DIVISION_SETTINGS_TEXTFIELD_LIMIT} characters`
                    }
                    placeholder="Enter task name"
                    InputProps={{ 
                              endAdornment: editingTaskNameTrim.length !== 0 ? (
                                   <InputAdornment position="end">
                                        <Tooltip title="Clear" arrow placement="bottom">
                                             <IconButton onClick={() => setEditingTaskName('')}>
                                                  <ClearIcon
                                                       style={{
                                                            fontSize: "12px",
                                                            stroke: "black",
                                                            strokeWidth: 1.5,
                                                       }}
                                                  />
                                             </IconButton>
                                        </Tooltip>
                                   </InputAdornment>
                              ) : null
                         }}
               />
               <Tooltip title="Save" arrow placement="bottom">
                    <span>
                         <IconButton
                              onClick={handleSave}
                              sx={{ color: '#2e7d32' }}
                              disabled={editingTaskName.length === 0 || editingTaskName.toLowerCase().trim() === taskName.toLowerCase().trim() || existingTaskNames?.some(nameOfTask => nameOfTask.trim().toLowerCase() === editingTaskName.trim().toLowerCase()) || editingTaskName.toLowerCase().trim() === milestoneName.toLowerCase().trim()}
                              aria-label="Save"
                         >
                              <SpellcheckIcon style={{ fontSize: '20px'}}/>
                         </IconButton>
                    </span>
               </Tooltip>
               <Tooltip title="Cancel" arrow placement="bottom">
                    <IconButton
                         onClick={handleCancel}
                         sx={{ color: '#d32f2f' }}
                    >
                         <CloseIcon style={{ fontSize: '20px' }}/>
                    </IconButton>
               </Tooltip>
          </Box>
     );
}; 